import { useEffect, useState } from "react";
import "./BgImage.scss";
import userAvatar from "@/core/assets/images/user-avatar.jpg";

interface iBgImage {
  img: string;
  position?: string;
  index?: number;
  altImage?: string;
}

export const BgImage = (props: iBgImage) => {
  const { img, position, index, altImage = userAvatar } = props;
  const [currentImg, setCurrentImg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    setIsLoading(true);
    let isMounted = true;

    const loadImage = (src: string): Promise<string> => {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.src = src;
        image.onload = () => resolve(src);
        image.onerror = (err) => reject(err);
      });
    };

    const loadWithFallback = async () => {
      try {
        const loadedSrc = await loadImage(img);
        if (isMounted) {
          setCurrentImg(loadedSrc);
        }
      } catch (error) {
        if (altImage) {
          try {
            const loadedSrc = await loadImage(altImage);
            if (isMounted) {
              setCurrentImg(loadedSrc);
            }
          } catch (altError) {
            console.warn("Both primary and alt image failed to load:", {
              img,
              altImage,
            });
            if (isMounted) {
              setCurrentImg(userAvatar); // Or a specific placeholder error image
            }
          }
        } else {
          console.warn("Image failed to load and no alt image provided:", img);
          if (isMounted) {
            setCurrentImg(null);
          }
        }
      }
    };

    loadWithFallback();

    return () => {
      isMounted = false;
    };
  }, [img, altImage]);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  return (
    <div
      className={`bgImage-container ${position || ""}`}
      style={{ zIndex: index ?? 1 }}
    >
      {/* This placeholder is always rendered */}
      <div className={`placeholder ${!isLoading ? "hidden" : ""}`} />

      {/* The image is loaded in the background and fades in */}
      {currentImg && (
        <img
          className={`img ${isLoading ? "loading" : "loaded"}`}
          src={currentImg}
          onLoad={handleImageLoad}
          alt="" // Alt text should be descriptive or empty for decorative images
        />
      )}
    </div>
  );
};
